var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import 'element-internals-polyfill';
import { LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import { submit } from './submit.js';
export function FormControlMixin(superclass) {
    class FormControl extends superclass {
        constructor() {
            super(...arguments);
            this._internals = this.attachInternals();
            /** If true, the input will be disabled */
            this.disabled = false;
            /** If true, the input is required if used in the context of a form */
            this.required = false;
            /**
             * If this is true it signals the input has been interacted with
             * @attr
             */
            this.dirty = false;
            /**
             * If this is true it signals the input has been focused
             * @attr
             */
            this.touched = false;
            /**
             * Error message to display when there is a malformed value
             * @attribute bad-input-message
             */
            this.badInputMessage = 'This field is invalid';
            /**
             * Error message to display with the pattern and value are mismatched
             * @attribute pattern-mismatch-error
             */
            this.patternMismatchMessage = 'This field does not follow the proper format';
            /**
             * Error message to display when the value overflows the given range
             * @attribute range-overflow-message
             */
            this.rangeOverflowMessage = 'This field does not follow the proper format';
            /**
             * Error message to display when the value underflows the given range
             * @attribute range-underflow-message
             */
            this.rangeUnderflowMessage = 'The value does not fit in the necessary range';
            /**
             * Error message to display when the value does not align with the given step
             * @attribute step-mismatch-message
             */
            this.stepMismatchMessage = 'The value is not a valid step';
            /**
             * Error message to display when the value is too long
             * @attribute too-long-message
             */
            this.tooLongMessage = 'The value is too long';
            /**
             * Error message to display when the value is too short
             * @attribute too-short-message
             */
            this.tooShortMessage = 'The value is too short';
            /**
             * Error message to display when the value does not match the expected type
             * @attribute type-mismatch-message
             */
            this.typeMismatchMessage = 'The entered value is not the right format';
            /**
             * Error message to display when the value is missing
             * @attribute value-missing-message
             */
            this.valueMissingMessage = 'This field is required';
            /** Tracks if the host element is currently focused */
            this.focused = false;
            /** Tracks if the error state should be forced */
            this.forceError = false;
            /**
             * If the enter key is pressed
             */
            this._onKeypressSubmit = (event) => {
                if (!this._internals.form) {
                    return;
                }
                if (event.code === 'Enter') {
                    this.reconcileValidity();
                    submit(this.form);
                }
            };
            /**
             * Sets the input to dirty and removes this listener
             * a field so binding happens automagically
             */
            this._setDirty = () => {
                if (!this.dirty) {
                    this.dirty = true;
                }
                this.forceError = false;
                this.reconcileValidity();
            };
            /**
             * Sets the input to touched and removes the listener
             * a field so binding happens auto-magically
             */
            this._setTouched = () => {
                if (!this.touched) {
                    this.touched = true;
                }
                this.focused = true;
                this.requestUpdate();
            };
            /** Reset focused and show validity state */
            this._onInputBlurCheck = () => {
                const gwcEvent = new CustomEvent('gwc-validate', { cancelable: true });
                this.input.dispatchEvent(gwcEvent);
                if (!gwcEvent.defaultPrevented) {
                    this.focused = false;
                    this.forceError = false;
                }
                // added to re-validated input after value as been formatted "masked"
                this.reconcileValidity();
            };
            /** When an invalid event is triggered, force the error state */
            this._onInvalid = (event) => {
                event.preventDefault(); // disable the default validation popup
                this.forceError = true;
            };
            /** Reset showError state */
            this._onFormReset = () => {
                this.forceError = false;
                this.touched = false;
                this.dirty = false;
            };
            /** A queue of ValidityState changes */
            this._queue = [];
            this.reconcileValue = (value) => {
                if (this.input && this.input.value !== value) {
                    this.input.value = value;
                    this.reconcileValidity();
                }
            };
            /** Reconcile the native element's validity state with the internals state */
            this.reconcileValidity = () => {
                if (!this.input) {
                    return;
                }
                if (!this.errorMessage && this.input.validity.valid && this._queue.length === 0) {
                    this._internals.setValidity({});
                }
                else {
                    /** Consolidate queued changes */
                    let queued = {};
                    this._queue.forEach((change) => (queued = { ...change }));
                    /** Object to merge internal validity and queued validity */
                    const validity = {};
                    /** Error key to display */
                    let invalidKey;
                    for (const key in this.input.validity) {
                        /** If there is a queued change, use it; otherwise, defer to this.input */
                        // eslint-disable-next-line no-prototype-builtins
                        if (queued.hasOwnProperty(key)) {
                            validity[key] = queued[key];
                        }
                        else {
                            validity[key] = this.input.validity[key];
                        }
                        /** Determine which error message to use */
                        if (key !== 'valid' && validity[key]) {
                            invalidKey = key;
                        }
                    }
                    if (this.errorMessage) {
                        validity.customError = true;
                    }
                    /** Set the host's validity state */
                    this._internals.setValidity(validity, this.errorMessage || this[`${invalidKey}Message`], this.input);
                }
                /** Empty the change queue */
                this._queue = [];
                this.requestUpdate();
            };
        }
        static get formAssociated() {
            return true;
        }
        /** If set, this will be used as the element's validation message */
        get errorMessage() {
            return this._errorMessage || '';
        }
        set errorMessage(message) {
            if (message) {
                // dont set the error message on initial prop set, because this.input
                // wont be available until firstUpdated
                this.forceError = true;
                this.setCustomValidity(message);
            }
            else {
                this.setCustomValidity();
            }
            this._errorMessage = message;
            this.requestUpdate();
        }
        set errorMessages(newErrorMessages) {
            const parsedMessages = typeof newErrorMessages === 'object' ? newErrorMessages : JSON.parse(newErrorMessages);
            if (Object.keys(parsedMessages).length > 0) {
                for (const [validityKey, errorMessage] of Object.entries(parsedMessages)) {
                    if (validityKey && this[`${validityKey}Message`]) {
                        this[`${validityKey}Message`] = errorMessage;
                    }
                }
            }
        }
        /**
         * Queue up a validity change to be evaluated during
         * validity reconciliation
         */
        queueValidity(state) {
            this._queue.push(state);
        }
        connectedCallback() {
            super.connectedCallback();
            const { form } = this;
            if (form) {
                form.addEventListener('reset', this._onFormReset);
            }
            this.addEventListener('invalid', this._onInvalid);
        }
        disconnectedCallback() {
            /** Periodically Angular creates then removes elements before the first update cycle completes */
            if (this.input) {
                this.input.removeEventListener('input', this._setDirty);
                this.input.removeEventListener('focus', this._setTouched);
                this.input.removeEventListener('change', this.reconcileValidity);
            }
            this.removeEventListener('invalid', this._onInvalid);
            this.removeEventListener('blur', this._onInputBlurCheck);
            if (this.form) {
                this.form.removeEventListener('reset', this._onFormReset);
            }
            super.disconnectedCallback();
        }
        /**
         * Link the input back to the mixin
         */
        linkControl(input) {
            if (!this.input && input) {
                this.input = input;
            }
            this.reconcileValidity();
            this.input.addEventListener('input', this._setDirty);
            this.input.addEventListener('focus', this._setTouched);
            this.input.addEventListener('keypress', this._onKeypressSubmit);
            this.input.addEventListener('change', this.reconcileValidity);
            this.addEventListener('blur', this._onInputBlurCheck);
        }
        /** Forward the internals checkValidity method */
        checkValidity() {
            return this._internals.checkValidity();
        }
        /** Forward the internals reportValidity method */
        reportValidity() {
            return this._internals.reportValidity();
        }
        /** The control's form */
        get form() {
            return this._internals.form;
        }
        /** Will return true when the error state needs to be shown */
        get showError() {
            /** dont show error messages if the form control is disabled
            so that we dont yell at users when they cant fix it */
            if (this.disabled) {
                return false;
            }
            /** otherwise, only show errors if a custom one is set or if the conditions are right */
            return this.forceError || (this.touched && this.validity && !this.validity.valid && !this.focused);
        }
        /** Forward the element internals validity state */
        get validity() {
            return this._internals.validity;
        }
        /** Forward the internals validation message */
        get validationMessage() {
            return this.errorMessage || this._internals.validationMessage;
        }
        get willValidate() {
            return this._internals.willValidate;
        }
        blur() {
            return this.input.blur();
        }
        click() {
            return this.input.click();
        }
        select() {
            return this.input.select();
        }
        updated(changed) {
            if (changed.has('required')) {
                this.forceError = false;
                this.reconcileValidity();
            }
        }
        /** De facto proxy for internals.setValidity for customErrors */
        setCustomValidity(message) {
            if (message) {
                const target = this.input;
                if (target) {
                    const validity = {};
                    for (const key in target.validity) {
                        if (key === 'valid' || key === 'customError') {
                            continue;
                        }
                        validity[key] = target.validity[key];
                    }
                    this._internals.setValidity({
                        ...validity,
                        customError: true
                    }, message, target);
                }
                else {
                    this._internals.setValidity({
                        customError: true
                    }, message);
                }
                this.forceError = true;
            }
            else {
                this._internals.setValidity({ customError: false }, this.validationMessage ? this.validationMessage : undefined);
                this.forceError = false;
            }
        }
    }
    __decorate([
        property({ type: Boolean, reflect: true })
    ], FormControl.prototype, "disabled", void 0);
    __decorate([
        property({ type: Boolean, reflect: true })
    ], FormControl.prototype, "required", void 0);
    __decorate([
        property({ type: Boolean, reflect: true })
    ], FormControl.prototype, "dirty", void 0);
    __decorate([
        property({ type: Boolean, reflect: true })
    ], FormControl.prototype, "touched", void 0);
    __decorate([
        property({ type: String, attribute: 'bad-input-message', reflect: false })
    ], FormControl.prototype, "badInputMessage", void 0);
    __decorate([
        property({ type: String, attribute: 'pattern-mismatch-message', reflect: false })
    ], FormControl.prototype, "patternMismatchMessage", void 0);
    __decorate([
        property({ type: String, attribute: 'range-overflow-message', reflect: false })
    ], FormControl.prototype, "rangeOverflowMessage", void 0);
    __decorate([
        property({ type: String, attribute: 'range-underflow-message', reflect: false })
    ], FormControl.prototype, "rangeUnderflowMessage", void 0);
    __decorate([
        property({ type: String, attribute: 'step-mismatch-message', reflect: false })
    ], FormControl.prototype, "stepMismatchMessage", void 0);
    __decorate([
        property({ type: String, attribute: 'too-long-message', reflect: false })
    ], FormControl.prototype, "tooLongMessage", void 0);
    __decorate([
        property({ type: String, attribute: 'too-short-message', reflect: false })
    ], FormControl.prototype, "tooShortMessage", void 0);
    __decorate([
        property({ type: String, attribute: 'type-mismatch-message', reflect: false })
    ], FormControl.prototype, "typeMismatchMessage", void 0);
    __decorate([
        property({ type: String, attribute: 'value-missing-message', reflect: false })
    ], FormControl.prototype, "valueMissingMessage", void 0);
    __decorate([
        state()
    ], FormControl.prototype, "focused", void 0);
    __decorate([
        state()
    ], FormControl.prototype, "forceError", void 0);
    __decorate([
        property({ type: String, attribute: 'error-message' })
    ], FormControl.prototype, "errorMessage", null);
    __decorate([
        property({ type: Object, attribute: 'error-messages' })
    ], FormControl.prototype, "errorMessages", null);
    return FormControl;
}
export const FormControl = FormControlMixin(LitElement);
//# sourceMappingURL=FormControlMixin.js.map